import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress, Divider } from "@mui/material";
import { PaymentComponentProps } from "./types";

// Import necessary utilities for URL manipulation and reset functionality
import useStore from "../../../Store/Store";
import { clear } from "idb-keyval";

const PaymentComponent: React.FC<PaymentComponentProps> = ({
  amount,
  description = "Premium Model Access",
  onPaymentSuccess,
  onPaymentError,
  onPaymentCancel,
}) => {
  const [processingMethod, setProcessingMethod] = useState<string | null>(null);

  const buttonContainerStyle = {
    width: "100%",
    maxWidth: "350px",
    height: "55px",
    borderRadius: "50px",
    marginBottom: "16px",
    position: "relative" as const,
    overflow: "hidden",
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "400px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
      }}
      className="sentry-ignore payment-main-container"
      data-sentry-ignore="true"
      data-sentry-block="true"
    >
      {/* Order Summary Section */}
      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "24px",
          border: "1px solid #e9ecef",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#2c3e50",
            fontWeight: 600,
            marginBottom: "16px",
            fontSize: "18px",
          }}
        >
          Order Summary
        </Typography>

        {/* Line Item */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <Box>
            <Typography
              variant="body1"
              sx={{
                color: "#495057",
                fontWeight: 500,
                fontSize: "15px",
                lineHeight: 1.3,
              }}
            >
              {description}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6c757d",
                fontSize: "13px",
                marginTop: "2px",
              }}
            >
              Single generation access
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#495057",
              fontWeight: 500,
              fontSize: "15px",
            }}
          >
            ${(amount / 100).toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ marginY: "16px", borderColor: "#dee2e6" }} />

        {/* Subtotal */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#6c757d",
              fontSize: "14px",
            }}
          >
            Subtotal
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6c757d",
              fontSize: "14px",
            }}
          >
            ${(amount / 100).toFixed(2)}
          </Typography>
        </Box>

        {/* Tax */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#6c757d",
              fontSize: "14px",
            }}
          >
            Tax
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6c757d",
              fontSize: "14px",
            }}
          >
            $0.00
          </Typography>
        </Box>

        <Divider sx={{ marginY: "12px", borderColor: "#dee2e6" }} />

        {/* Total */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#2c3e50",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Total
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#2c3e50",
              fontWeight: 700,
              fontSize: "18px",
            }}
          >
            ${(amount / 100).toFixed(2)} USD
          </Typography>
        </Box>
      </Box>

      {/* Override Button - Skip Payment */}
      <div
        className="payment-container override-container sentry-ignore"
        data-payment-service="override"
        data-sentry-ignore="true"
        data-sentry-block="true"
        style={buttonContainerStyle}
      >
        <button
          onClick={async () => {
            setProcessingMethod("override");

            try {
              // Get the postToDrawioWaitForResponse function from the store
              const { postToDrawioWaitForResponse } = useStore.getState();

              // 1. Prepare the new URL with model param and without simulation param
              const url = new URL(window.location.href);

              // 2. Strip out the "simulation" param and "model" param from the URL (if there is one)
              url.searchParams.delete("simulation");
              url.searchParams.delete("model");

              // 3. Append to the URL the param &model=gemini-2.5-pro
              url.searchParams.set("model", "gemini-2.5-pro");

              // 4. Set the regenerateWithPro flag to skip loading existing diagram
              url.searchParams.set("regenerateWithPro", "true");

              // 4. Perform reset cache functionality
              localStorage.clear();
              sessionStorage.clear();
              // Clear CodeCanvas Cache
              await clear();

              // Clear Drawio's Cache
              await postToDrawioWaitForResponse({
                action: "RESET_CACHE",
              });

              // 5. Load to the new URL with the model param set and without the simulation param
              // Add reloadTime param to prevent browser's auto caching
              url.searchParams.set("reloadTime", Date.now().toString());

              // Navigate to the new URL
              window.location.href = url.toString();
            } catch (error) {
              console.error("Error during free override:", error);
              setProcessingMethod(null);
              onPaymentSuccess({
                method: "override",
                transactionId: `override_${Date.now()}`,
                amount: 0, // No cost for override
                currency: "USD",
              });
            }
          }}
          disabled={!!processingMethod}
          className="sentry-ignore payment-button override-button"
          data-sentry-ignore="true"
          data-sentry-block="true"
          style={{
            width: "100%",
            height: "55px",
            borderRadius: "50px",
            backgroundColor: "#FFAEA6",
            color: "#fff",
            border: "2px solid #FFAEA6",
            fontFamily: "Arial, sans-serif",
            fontSize: "16px",
            fontWeight: "600",
            cursor: processingMethod ? "not-allowed" : "pointer",
            opacity: processingMethod ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            if (!processingMethod) {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#FFAEA6";
            }
          }}
          onMouseLeave={(e) => {
            if (!processingMethod) {
              e.currentTarget.style.backgroundColor = "#FFAEA6";
              e.currentTarget.style.color = "#fff";
            }
          }}
        >
          {processingMethod === "override" && (
            <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
          )}
          Use Pro Model (Free Override)
        </button>

        {processingMethod === "override" && (
          <div
            className="sentry-ignore payment-overlay"
            data-sentry-ignore="true"
            data-sentry-block="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 174, 166, 0.8)",
              borderRadius: "50px",
              zIndex: 10,
            }}
          >
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          </div>
        )}
      </div>
    </Box>
  );
};

export default PaymentComponent;
